
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SalePrint from './Print';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';


function SaleIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();

    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');

    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const branchId = 0;
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {

        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);


    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/SaleCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/SaleEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }

        else if (location.state?.from === '/SaleDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
            // handleClickOpen(false);
        }
    }, [location]);

    const fetchtableData = async () => {

        setData([]);
        try {
            const response = await fetchData('SaleApi', `/SaleDetails/${companyId}/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const searchableFields = ['BillPrefix', 'FinYear', 'BillNumber', 'BillDate', 'CustomerName', 'TotalQuantity', 'TaxableAmount', 'CGSTAmount', 'SGSTAmount', 'IGSTAmount', 'GrandTotal', 'TotalQuantity'];

    const filteredData = data.filter((item) =>
        searchableFields.some((key) =>
            item[key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
    );


    const handleDelete = async () => {
        deleteData('SaleApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SaleApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handlePrint = async (SALEID) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <SalePrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };


    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>

            <div className='w-full mb-2 flex'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Sale </h6>
                <div style={{ marginLeft: '-30px', marginBottom: '10px', marginTop: '20px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '10px', width: '250px' }}
                        />
                    )}
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        From
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        To
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        padding: '5px 10px',
                        border: '1px solid blue',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '30px',
                        marginLeft: '15px',
                        marginTop: '1.50%',
                        textTransform: 'uppercase'
                    }}
                    onClick={fetchtableData}
                    className="linkcolor"
                >
                    <Tooltip title="Get Sale Details" arrow>
                        Refresh
                    </Tooltip>
                </Button>

                {hasCreateMasterPermission && (
                    <Button

                        component={Link}
                        to="/SaleCreate"
                        className="linkcolor"
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            padding: '5px 10px',
                            border: '1px solid black',
                            textTransform: 'none',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                            maxHeight: '30px',
                            marginLeft: '15px',
                            marginTop: '1.50%',
                            textTransform: 'uppercase'

                        }}
                    >
                        <Tooltip title="Create New Sale" arrow>
                            CREATE NEW
                        </Tooltip>

                    </Button>
                )}


            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: 'teal' }}>
                            <TableRow>
                                <TableCell className='table-header-cell'>Sl.No</TableCell>
                                <TableCell className='table-header-cell'>BILL DATE</TableCell>
                                <TableCell className='table-header-cell'>BILL NO</TableCell>
                                <TableCell className='table-header-cell'>NAME</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>AMOUNT</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>CGST</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>SGST</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>IGST</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>TOTAL</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}>QTY</TableCell>
                                <TableCell className='table-header-cell' style={{ textAlign: 'right' }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: 'white' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: '#E3FEF7', // Change color on hover
                                        },
                                    }}
                                >
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{index + 1}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{item.BillPrefix}/{item.BillNumber}/{item.FinYear}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{item.CustomerName}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.TaxableAmount}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.CGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.SGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.IGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.GrandTotal}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>

                                        {hasCreateMasterPermission && (
                                            <Tooltip
                                                title="Sale Return" arrow>
                                                <IconButton component={Link} to={`/SaleReturnCreate/${item.Id}`} color="primary">
                                                    <AssignmentReturnIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        <Tooltip title="Sale Details" arrow>
                                            <IconButton component={Link} to={`/SaleDetails/${item.Id}`} color="warning">
                                                <Info />
                                            </IconButton>
                                        </Tooltip>

                                        {hasCreateMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale Print" arrow>
                                                <IconButton onClick={() => handlePrint(item.Id)}>
                                                    <PrintIcon style={{ height: '22px', color: 'darkmagenta' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale Edit" arrow>
                                                <IconButton component={Link} to={`/SaleEdit/${item.Id}`} color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale Delete" arrow>
                                                <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {!item.IsActive && (
                                            <Tooltip title="Sale Activate" arrow>
                                                <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                    <ActivateIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Confirmation Dialog */}

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>

            </Dialog>

        </div>
    );
}

export default SaleIndex;



